/**
 * WebPlacementConfigLoader
 *
 * Loads V2 onboarding version data for web placement deployment.
 *
 * Storage architecture:
 * - Step nodes stored in the screensV2 subcollection, not in the version document
 * - Path: projects/{projectId}/onboardings/{onboardingId}/versions/{versionId}/screensV2/{screenId}
 *
 * @module webplacements/services
 */

import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { StepNodeScreenService } from '@modules/firebase-core/services/StepNodeScreenService';
import type { WebPlacement, WebPlacementConfig } from '@modules/webplacements/types/webplacements.types';
import type { FlowDocument } from '@segmently/schema-models/v2';
import {
  getExplicitSchemaVersion,
  inferV2SchemaVersionFromParent,
  isV2SchemaVersion,
} from './schemaVersionSupport';

/**
 * Result of config loading
 */
export interface WebPlacementLoadResult {
  /** Loaded V2 funnel document for publish */
  flowDocument: FlowDocument;
  /** Funnel ID */
  funnelId: string;
  /** Version ID */
  versionId: string;
  /** WebPlacement config for baking (parameters, SEO, analytics, etc.) */
  webPlacementConfig: WebPlacementConfig;
}

/**
 * WebPlacementConfigLoader
 *
 * V2-only config loader for single web placements.
 *
 * Usage:
 * ```typescript
 * const loader = new WebPlacementConfigLoader();
 * const result = await loader.loadConfig(projectId, webPlacement);
 * // result.flowDocument contains the V2 FlowDocument for publish
 * ```
 */
export class WebPlacementConfigLoader {
  private readonly onboardingService: OnboardingService;
  private readonly stepNodeScreenService: StepNodeScreenService;

  constructor(
    onboardingService?: OnboardingService,
    stepNodeScreenService?: StepNodeScreenService
  ) {
    this.onboardingService = onboardingService || new OnboardingService();
    this.stepNodeScreenService = stepNodeScreenService || new StepNodeScreenService();
  }

  /**
   * Load full V2 FlowDocument for web placement.
   *
   * @param projectId - Project ID
   * @param webPlacement - Web placement with config
   * @returns Load result with full FlowDocument
   * @throws Error if version is not V2, has no screensV2, or has an invalid launchScreenId
   */
  async loadConfig(
    projectId: string,
    webPlacement: WebPlacement
  ): Promise<WebPlacementLoadResult> {
    const config = webPlacement.config;

    if (!config) {
      throw new Error(`Web placement "${webPlacement.id}" has no config.`);
    }

    const { funnelId, versionId } = config;

    if (!funnelId || !versionId) {
      throw new Error(
        `Web placement "${webPlacement.id}" has incomplete config: ` +
        `funnelId=${funnelId}, versionId=${versionId}`
      );
    }

    const flowDocument = await this.loadFlowDocument(
      projectId,
      funnelId,
      versionId,
      webPlacement.name
    );

    return {
      flowDocument,
      funnelId,
      versionId,
      webPlacementConfig: config,
    };
  }

  /**
   * Build full FlowDocument by loading version metadata + screensV2 subcollection.
   *
   * @param projectId - Project ID
   * @param onboardingId - Onboarding (funnel) ID
   * @param versionId - Version ID
   * @param placementName - For error messages
   * @returns Full V2 FlowDocument
   */
  private async loadFlowDocument(
    projectId: string,
    onboardingId: string,
    versionId: string,
    placementName: string
  ): Promise<FlowDocument> {
    const version = await this.onboardingService.getVersion(
      projectId,
      onboardingId,
      versionId
    );

    if (!version) {
      throw new Error(
        `Version not found for web placement "${placementName}": ` +
        `onboarding=${onboardingId}, version=${versionId}`
      );
    }

    const explicitSchemaVersion = getExplicitSchemaVersion(version);
    let schemaVersion = explicitSchemaVersion;

    if (schemaVersion && !isV2SchemaVersion(schemaVersion)) {
      throw new Error(
        `Cannot deploy web placement "${placementName}": ` +
        `selected onboarding version must use schemaVersion 2.x. ` +
        `Current schemaVersion: ${schemaVersion}.`
      );
    }

    if (!schemaVersion) {
      const onboarding = await this.onboardingService.getOnboarding(projectId, onboardingId);
      const inferredSchemaVersion = inferV2SchemaVersionFromParent(onboarding);
      if (!inferredSchemaVersion) {
        throw new Error(
          `Cannot deploy web placement "${placementName}": ` +
          `selected onboarding version must use schemaVersion 2.x. ` +
          `Current schemaVersion: missing.`
        );
      }
      schemaVersion = inferredSchemaVersion;
    }

    if (!isV2SchemaVersion(schemaVersion)) {
      throw new Error(
        `Cannot deploy web placement "${placementName}": ` +
        `selected onboarding version must use schemaVersion 2.x. ` +
        `Current schemaVersion: ${schemaVersion || 'missing'}.`
      );
    }

    const stepNodes = await this.stepNodeScreenService.listScreens(
      projectId,
      onboardingId,
      versionId
    );

    if (stepNodes.length === 0) {
      throw new Error(
        `Cannot deploy web placement "${placementName}": ` +
        `selected V2 onboarding version has no screensV2 data.`
      );
    }

    const launchScreenId = version.screensGraph?.launchScreenId;
    if (!launchScreenId) {
      throw new Error(
        `Cannot deploy web placement "${placementName}": ` +
        `selected V2 onboarding version has no launchScreenId.`
      );
    }

    if (!stepNodes.find((s) => s.id === launchScreenId)) {
      throw new Error(
        `Cannot deploy web placement "${placementName}": ` +
        `launch screen "${launchScreenId}" was not found in screensV2.`
      );
    }

    const variables = version.variables as Record<string, unknown> | undefined;
    // Global default screen transition (schema 2.17.0) — stored on the version
    // metadata alongside launchScreenId; must survive the bake or the web-shell
    // runtime can never see it (Route.transition passes through inside screens).
    const defaultTransition = version.screensGraph?.defaultTransition as
      | FlowDocument['defaultTransition']
      | undefined;
    // Global default screen ENTRANCE animation (schema 2.17.0) — same bake
    // requirement as defaultTransition: without it the runtime fallback
    // screenAnimation > animated:true > defaultScreenAnimation never resolves.
    const defaultScreenAnimation = version.screensGraph?.defaultScreenAnimation as
      | FlowDocument['defaultScreenAnimation']
      | undefined;
    // Per-StepKind transition defaults (local FlowDocument extension, see
    // themes/animation/applyAnimationSettingsToFlowDocument.ts) — resolved at
    // navigation time against the TARGET screen's kind, between Route.transition
    // and defaultTransition. Same bake requirement as the two fields above.
    const perKindTransitions = (version.screensGraph as Record<string, unknown> | undefined)
      ?.perKindTransitions as Record<string, unknown> | undefined;
    const flowDocument: FlowDocument = {
      schemaVersion,
      defaultLanguage: (version.screensGraph?.defaultLanguage ?? 'en-US') as FlowDocument['defaultLanguage'],
      languages: (version.screensGraph?.languages ?? ['en-US']) as FlowDocument['languages'],
      launchScreenId,
      screens: Object.fromEntries(stepNodes.map((s) => [s.id, s])),
      metadata: (version.screensGraph?.metadata ?? {}) as FlowDocument['metadata'],
      variables: (variables ?? {}) as FlowDocument['variables'],
      ...(defaultTransition ? { defaultTransition } : {}),
      ...(defaultScreenAnimation ? { defaultScreenAnimation } : {}),
      ...(perKindTransitions ? { perKindTransitions } : {}),
    };

    const varCount = variables ? Object.keys(variables).length : 0;
    console.log(
      `[WebPlacementConfigLoader] V2 funnel: ${stepNodes.length} StepNodes, ${varCount} variables loaded.` +
      (varCount > 0 ? ` IDs: ${Object.keys(variables!).join(', ')}` : ' (none)')
    );

    return flowDocument;
  }
}
